import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { createPortal } from 'react-dom'

import { Icon } from '@iconify/react'
import InfiniteScroll from 'react-infinite-scroll-component'

// antd
import {
    Typography,
    Button,
    Row,
    Col,
    theme,
    Input,
    Tooltip,
    Popover,
    Dropdown,
} from 'antd'

// slices
import {
    taskListSupport,
    taskCreateSupport,
    taskUpdateSupport,
    mainTaskList,
    mainTaskCreate,
    mainTaskUpdate,
    taskDeleteSupport,
    mainTaskDelete,
} from '../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
    resolveReferenceId,
} from '../taskRefactored/tasks.service'
import {
    projectsState,
    createSwimlaneAction,
    changeSwimlanesPositionAction,
} from '../projects/projects.slice'
import { projectTaskListAssigneeUser } from '../users/users.slice'

// components
import CreateTaskCard from './components/CreateTaskCard'
import DateRangePicker from './components/DateRangePicker'
import DeleteSectionModal from './components/DeleteSectionModal'
import TaskSubTaskAccordion from './components/TaskSubTaskAccordion'
import { showActionNotification } from '../../components/notification'
import AssigneeSelectorButton from './components/AssigneeSelectorButton'
import EpicSelector, {
    getEpicDisplayOptionFromTask,
} from '../taskRefactored/components/epicSelector'
import TypeSelector from '../taskRefactored/components/typeSelector'
import TaskParentEpicModal from './components/TaskParentEpicModal'
import { confirmDeleteTask, getDeleteTaskMenuLabel } from './components/confirmDeleteTask'
import { useTaskListKindPermission } from './projectPermissions'
import unicornFxImage from '../../assets/images/task/unicorn.png'

// styles
import styles from './projectDetail.module.css'
import { getColumnColor } from '../../utils/commonUtils'
import { PRIVATE_ROUTES, buildProjectTaskTabSearch } from '../../routes'

const { Text } = Typography
const { TextArea } = Input

const TASK_TYPE_OPTIONS = [
    { label: 'Task', value: 'Task' },
    { label: 'Bug', value: 'Bug' },
]

const PENDING_TASK_PREFIX = 'pending-create:'

const isPendingCreateTask = (task) =>
    Boolean(
        task?.pendingCreate ||
        String(task?._id || '').startsWith(PENDING_TASK_PREFIX)
    )

const createPendingTaskId = () =>
    `${PENDING_TASK_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const SECTION_NAME_MAX_DISPLAY = 30
const SECTION_NAME_MAX_DISPLAY_ON_HOVER = 22

const formatSectionNameDisplay = (
    name,
    maxLength = SECTION_NAME_MAX_DISPLAY
) => {
    const text = String(name ?? '')
    if (text.length <= maxLength) return text
    return `${text.slice(0, maxLength)}...`
}

const getTaskEpicId = (task) => resolveReferenceId(task?.epic ?? task?.epicId)

const copyTaskContextLink = async (text) => {
    try {
        if (!text) return false
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }
    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.top = '-1000px'
        el.style.left = '-1000px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}

const ProjectTaskList = ({
    projectTaskList,
    projectTaskListStatus,
    projectTaskListPage,
    projectTaskListIsNext,
    projectId,
    onTaskClick,
    taskDetailVisible,
    onAddTaskToFirstSwimlane,
    taskListKind = 'support',
    taskRouteView = 'board',
    liveTaskTitlePatch = null,
    onRefreshTasks,
    listQuery = null,
}) => {
    const dispatch = useDispatch()
    const isMain = taskListKind === 'main'
    const { canEdit: canEditModule, canDelete: canDeleteModule } =
        useTaskListKindPermission(taskListKind)
    const swimlaneTypePayload = useMemo(
        () => (isMain ? { type: 'pms' } : {}),
        [isMain]
    )
    const { useToken } = theme
    const { token } = useToken()
    const { currentProjectData } = useSelector(projectsState)

    const [createTaskStates, setCreateTaskStates] = useState({})
    const [filteredAssignees] = useState([])
    const [isAddingSection, setIsAddingSection] = useState(false)
    const [newSectionName, setNewSectionName] = useState('')
    const [isCreatingSection, setIsCreatingSection] = useState(false)
    const sectionInputRef = useRef(null)
    const [localTaskList, setLocalTaskList] = useState(projectTaskList)
    const localTaskListRef = useRef(projectTaskList)
    const pendingCardReordersRef = useRef(0)
    const pendingColumnReordersRef = useRef(0)
    const [sectionMenuOpen, setSectionMenuOpen] = useState({})
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deleteSectionData, setDeleteSectionData] = useState(null)
    const [deleteOption, setDeleteOption] = useState('keep') // 'keep' or 'delete'
    const [editingSectionId, setEditingSectionId] = useState(null)
    const [editingSectionName, setEditingSectionName] = useState('')
    const [hoveredSectionId, setHoveredSectionId] = useState(null)
    const sectionNameInputRef = useRef(null)

    useEffect(() => {
        localTaskListRef.current = localTaskList
    }, [localTaskList])

    const mapProjectTaskListToLocal = useCallback((list) => {
        if (!list) return list
        return list.map((group) => ({
            ...group,
            list: Array.isArray(group?.list)
                ? group.list.filter((task) => !isPendingCreateTask(task))
                : group?.list,
        }))
    }, [])

    // Sync localTaskList with projectTaskList (drops any stale pending-create placeholders)
    useEffect(() => {
        if (!projectTaskList) return
        if (
            pendingCardReordersRef.current > 0 ||
            pendingColumnReordersRef.current > 0
        ) {
            return
        }
        const mapped = mapProjectTaskListToLocal(projectTaskList)
        setLocalTaskList(mapped)
        localTaskListRef.current = mapped
    }, [projectTaskList, mapProjectTaskListToLocal])

    useEffect(() => {
        if (!liveTaskTitlePatch?.taskId) return
        setLocalTaskList((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev
            let changed = false
            const next = prev.map((group) => {
                if (!Array.isArray(group?.list) || group.list.length === 0) {
                    return group
                }
                const updatedList = group.list.map((task) => {
                    if (task?._id !== liveTaskTitlePatch.taskId) return task
                    changed = true
                    return {
                        ...task,
                        title: liveTaskTitlePatch.title,
                    }
                })
                return changed ? { ...group, list: updatedList } : group
            })
            return changed ? next : prev
        })
    }, [liveTaskTitlePatch])

    const refreshTasks = useCallback(
        (skip = 0) => {
            if (onRefreshTasks) {
                onRefreshTasks({}, { skip })
                return
            }
            if (isMain) {
                dispatch(
                    mainTaskList({
                        projectId,
                        skip,
                        limit: 10,
                        sort: { field: 'created', order: 'desc' },
                    })
                )
            } else {
                dispatch(
                    taskListSupport({
                        skip,
                        limit: 10,
                        filter: { projectId },
                        listType: 'status',
                    })
                )
            }
        },
        [dispatch, isMain, onRefreshTasks, projectId]
    )

    const fetchMoreTasks = useCallback(() => {
        if (projectTaskListIsNext && projectTaskListStatus !== 'loading') {
            const nextPage = projectTaskListPage + 1
            const skip = (nextPage - 1) * 10

            if (onRefreshTasks) {
                onRefreshTasks({}, { skip })
                return
            }

            if (isMain) {
                dispatch(
                    mainTaskList({
                        projectId,
                        skip,
                        limit: 10,
                        sort: { field: 'created', order: 'desc' },
                    })
                )
            } else {
                dispatch(
                    taskListSupport({
                        skip: skip,
                        limit: 10,
                        filter: {
                            projectId: projectId,
                        },
                        listType: 'status',
                    })
                )
            }
        }
    }, [
        dispatch,
        projectTaskListIsNext,
        projectTaskListStatus,
        projectTaskListPage,
        projectId,
        isMain,
        onRefreshTasks,
    ])

    const handleAddSection = () => {
        setIsAddingSection(true)
        setNewSectionName('')
        setTimeout(() => {
            sectionInputRef.current?.focus()
        }, 0)
    }

    const createSwimlane = async (sectionName) => {
        if (!sectionName || !sectionName.trim()) {
            setIsAddingSection(false)
            return
        }

        const trimmedSectionName = sectionName.trim()

        if (!projectId) {
            showActionNotification({
                type: 'error',
                message: 'Project ID is required',
            })
            setIsAddingSection(false)
            return
        }

        const existingSections = localTaskList || projectTaskList || []
        const isDuplicateSection = existingSections.some((group) => {
            const existingName = (group?._id || group?.name || '').trim()
            return (
                existingName.toLowerCase() === trimmedSectionName.toLowerCase()
            )
        })

        if (isDuplicateSection) {
            showActionNotification({
                type: 'error',
                message: 'Section name already exists',
                subMessage: trimmedSectionName,
            })
            return
        }

        // Calculate position based on existing swimlanes
        let newPosition = 0
        if (Array.isArray(projectTaskList) && projectTaskList.length > 0) {
            const maxPosition = Math.max(
                ...projectTaskList.map((group) => group?.position ?? 0)
            )
            newPosition = maxPosition + 1
        }

        setIsCreatingSection(true)

        try {
            setLocalTaskList((prev) => [
                ...prev,
                {
                    _id: trimmedSectionName,
                    name: trimmedSectionName,
                    position: newPosition.toString(),
                },
            ])

            const result = await dispatch(
                createSwimlaneAction({
                    id: projectId,
                    name: trimmedSectionName,
                    position: newPosition.toString(),
                    ...swimlaneTypePayload,
                })
            )

            if (result?.payload?.data?.success) {
                showActionNotification({
                    type: 'success',
                    message: 'Section created successfully',
                    subMessage: trimmedSectionName,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-created',
                })
                setNewSectionName('')
                setIsAddingSection(false)

                refreshTasks(0)
            } else {
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to create section',
                })
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to create section',
            })
        } finally {
            setIsCreatingSection(false)
        }
    }

    const handleSectionInputBlur = (e) => {
        // Use setTimeout to allow click events to process first
        setTimeout(() => {
            if (newSectionName.trim()) {
                createSwimlane(newSectionName)
            } else {
                setIsAddingSection(false)
            }
        }, 200)
    }

    const handleSectionInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (newSectionName.trim()) {
                createSwimlane(newSectionName)
            }
        } else if (e.key === 'Escape') {
            setIsAddingSection(false)
            setNewSectionName('')
        }
    }

    const handleSectionMenuOpenChange = (sectionId, open) => {
        setSectionMenuOpen((prev) => ({
            ...prev,
            [sectionId]: open,
        }))
    }

    const handleRenameSection = (sectionId) => {
        // Find the section to get its current name
        const section = (localTaskList || projectTaskList).find(
            (group) => group?._id === sectionId
        )
        if (section) {
            setEditingSectionId(sectionId)
            setEditingSectionName(sectionId || 'Unassigned')
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
            // Focus the input after state update
            setTimeout(() => {
                sectionNameInputRef.current?.focus()
                sectionNameInputRef.current?.select()
            }, 0)
        }
    }

    const handleSectionNameClick = (sectionId, e) => {
        if (!canEditModule) return
        // Don't trigger if clicking on the count or if already editing
        if (editingSectionId === sectionId) return
        e.stopPropagation()
        handleRenameSection(sectionId)
    }

    const handleSectionNameBlur = async (sectionId) => {
        if (!editingSectionId || editingSectionId !== sectionId) return

        const trimmedName = editingSectionName.trim()
        if (!trimmedName) {
            // Revert to original name if empty
            setEditingSectionName(sectionId || 'Unassigned')
            setEditingSectionId(null)
            return
        }

        // Only update if name changed
        if (trimmedName !== sectionId) {
            try {
                setLocalTaskList((prev) =>
                    prev.map((group) =>
                        group?._id === sectionId
                            ? { ...group, _id: trimmedName }
                            : group
                    )
                )

                const result = await dispatch(
                    createSwimlaneAction({
                        id: projectId,
                        fieldName: 'update',
                        oldName: sectionId,
                        newName: trimmedName,
                        ...swimlaneTypePayload,
                    })
                )

                if (result?.payload?.data?.success) {
                    showActionNotification({
                        type: 'success',
                        message: 'Section renamed successfully',
                        subMessage: `${sectionId} → ${trimmedName}`,
                        actionButtonText: 'Undo',
                        notificationKey: 'section-renamed',
                    })

                    // Reset editing state
                    setEditingSectionId(null)
                    setEditingSectionName('')

                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to rename section',
                    })
                    // Revert to original name on error
                    setEditingSectionName(sectionId || 'Unassigned')
                    setEditingSectionId(null)
                }
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to rename section',
                })
                // Revert to original name on error
                setEditingSectionName(sectionId || 'Unassigned')
            }
        }

        setEditingSectionId(null)
    }

    const handleSectionNameKeyDown = (sectionId, e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSectionNameBlur(sectionId)
        } else if (e.key === 'Escape') {
            // Revert to original name
            setEditingSectionName(sectionId || 'Unassigned')
            setEditingSectionId(null)
        }
    }

    const handleDeleteSection = (sectionId) => {
        // Find the section/group data
        const section = (localTaskList || projectTaskList).find(
            (group) => group?._id === sectionId
        )

        if (!section) {
            showActionNotification({
                type: 'error',
                message: 'Section not found',
            })
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
            return
        }

        // Calculate task counts
        const tasks = section?.list || []

        setDeleteSectionData({
            sectionId,
            sectionName: sectionId || 'Unassigned',
            totalTasks: tasks.length,
            position: section?.position ?? 0,
        })
        setDeleteOption('keep') // Default to keeping tasks
        setDeleteModalOpen(true)
        setSectionMenuOpen((prev) => ({
            ...prev,
            [sectionId]: false,
        }))
    }

    const handleConfirmDeleteSection = async () => {
        if (!deleteSectionData) return

        try {
            setLocalTaskList((prev) =>
                prev.filter(
                    (group) => group?._id !== deleteSectionData.sectionId
                )
            )

            const result = await dispatch(
                createSwimlaneAction({
                    id: projectId,
                    fieldName: 'remove',
                    name: deleteSectionData.sectionName,
                    position: deleteSectionData.position,
                    deleteTasks: deleteOption === 'delete',
                    ...swimlaneTypePayload,
                })
            )

            if (result?.payload?.data?.success) {
                showActionNotification({
                    type: 'success',
                    message: 'Section deleted successfully',
                    subMessage: deleteSectionData.sectionName,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-deleted',
                })
                setDeleteModalOpen(false)
                setDeleteSectionData(null)

                refreshTasks(0)
            } else {
                showActionNotification({
                    message: 'Failed to delete section',
                    subMessage: result?.payload?.data?.message,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-deleted',
                })
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to delete section',
            })
        }
    }

    const renderSectionMenu = (sectionId) => {
        // Calculate total number of swimlanes/sections
        const totalSections =
            (localTaskList || projectTaskList)?.filter(
                (group) => group?.list !== undefined
            )?.length || 0
        const isOnlyOneSection = totalSections <= 1

        return (
            <div className={styles.sectionMenu}>
                <div
                    className={styles.menuItem}
                    onClick={() => handleRenameSection(sectionId)}
                >
                    <Icon
                        icon="mdi:pencil"
                        width={16}
                        height={16}
                        style={{ color: '#64748b' }}
                    />
                    <span>Rename section</span>
                </div>

                {canDeleteModule && (
                <div
                    className={`${styles.menuItem} ${styles.deleteMenuItem} ${
                        isOnlyOneSection ? styles.disabledMenuItem : ''
                    }`}
                    onClick={
                        isOnlyOneSection
                            ? undefined
                            : () => handleDeleteSection(sectionId)
                    }
                    style={{
                        cursor: isOnlyOneSection ? 'not-allowed' : 'pointer',
                        opacity: isOnlyOneSection ? 0.5 : 1,
                    }}
                >
                    <Icon
                        icon="fluent:delete-28-regular"
                        width={16}
                        height={16}
                        style={{
                            color: isOnlyOneSection ? '#cbd5e1' : '#ef4444',
                        }}
                    />
                    <span
                        style={{
                            color: isOnlyOneSection ? '#cbd5e1' : '#ef4444',
                        }}
                    >
                        Delete section
                    </span>
                </div>
                )}
            </div>
        )
    }

    // Reorder swimlanes (columns) — optimistic local updates; API runs in background
    const orderColumns = useCallback(
        (initial, final) => {
            const currentList = localTaskListRef.current
            if (!currentList || currentList.length === 0) return

            const newTasks = structuredClone(currentList)
            newTasks.forEach((column) => {
                if (column.position === initial) {
                    column.position = final
                } else {
                    if (
                        column.position < Math.min(initial, final) ||
                        column.position > Math.max(initial, final)
                    ) {
                        return
                    }
                    if (initial < final) {
                        column.position--
                    } else {
                        column.position++
                    }
                }
            })
            newTasks.sort((a, b) => a.position - b.position)

            localTaskListRef.current = newTasks
            setLocalTaskList(newTasks)

            pendingColumnReordersRef.current += 1

            const persistReorder = async () => {
                try {
                    const result = await dispatch(
                        changeSwimlanesPositionAction({
                            initialPosition: initial,
                            finalPosition: final,
                            projectId: projectId,
                            ...swimlaneTypePayload,
                        })
                    )

                    const data = result?.payload?.data
                    if (!data?.success) {
                        showActionNotification({
                            type: 'error',
                            message:
                                data?.message || 'Failed to reorder swimlanes',
                        })
                        refreshTasks(0)
                    }
                } catch (error) {
                    showActionNotification({
                        type: 'error',
                        message: 'Failed to reorder swimlanes',
                    })
                    refreshTasks(0)
                } finally {
                    pendingColumnReordersRef.current = Math.max(
                        0,
                        pendingColumnReordersRef.current - 1
                    )
                }
            }

            void persistReorder()
        },
        [dispatch, projectId, refreshTasks, swimlaneTypePayload]
    )

    // Reorder tasks (cards) — optimistic local updates; API runs in background
    const orderCards = useCallback(
        (source, destination, cardId) => {
            const currentList = localTaskListRef.current
            if (!currentList || currentList.length === 0) return

            const findCardSnapshot = (listState, id) => {
                for (const group of listState || []) {
                    const t = group?.list?.find((c) => c._id === id)
                    if (t) return { ...t }
                }
                return null
            }

            const cardSnapshot = findCardSnapshot(currentList, cardId)
            const newTasks = structuredClone(currentList)
            let reorderApplied = false

            if (source.droppableId === destination.droppableId) {
                const column = newTasks.find(
                    (x) => x?._id === source.droppableId
                )
                if (!column?.list) return

                column.list.forEach((card) => {
                    if (card.position === source.index) {
                        card.position = destination.index
                    } else {
                        if (
                            card.position <
                                Math.min(source.index, destination.index) ||
                            card.position >
                                Math.max(source.index, destination.index)
                        ) {
                            return
                        }
                        if (source.index < destination.index) {
                            card.position--
                        } else {
                            card.position++
                        }
                    }
                })

                column.list.sort((a, b) => a.position - b.position)
                reorderApplied = true
            } else {
                const sourceColumnIndex = newTasks.findIndex(
                    (x) => x?._id === source.droppableId
                )
                const destinationColumnIndex = newTasks.findIndex(
                    (x) => x?._id === destination.droppableId
                )

                if (sourceColumnIndex === -1 || destinationColumnIndex === -1) {
                    return
                }

                const sourceCards = newTasks[sourceColumnIndex].list || []
                const destinationCards =
                    newTasks[destinationColumnIndex].list || []

                const cardIndex = sourceCards.findIndex(
                    (x) => x?._id === cardId
                )
                if (cardIndex === -1) return

                const draggedCard = sourceCards[cardIndex]

                sourceCards.forEach((card) => {
                    if (card.position > source.index) {
                        card.position--
                    }
                })

                if (destinationCards?.length > 0) {
                    destinationCards.forEach((card) => {
                        if (card.position >= destination.index) {
                            card.position++
                        }
                    })
                }

                sourceCards.splice(cardIndex, 1)
                draggedCard.position = destination.index
                draggedCard.status = destination.droppableId

                if (destinationCards?.length > 0) {
                    destinationCards.splice(destination.index, 0, draggedCard)
                } else {
                    destinationCards.push(draggedCard)
                }

                sourceCards.sort((a, b) => a.position - b.position)
                if (destinationCards?.length > 0) {
                    destinationCards.sort((a, b) => a.position - b.position)
                }
                reorderApplied = true
            }

            if (!reorderApplied) return

            localTaskListRef.current = newTasks
            setLocalTaskList(newTasks)

            pendingCardReordersRef.current += 1

            const persistReorder = async () => {
                try {
                    let data
                    if (isMain && cardSnapshot) {
                        const body = buildMainTaskUpdateBodyFromTask(
                            cardSnapshot,
                            projectId,
                            {
                                initialPosition: source?.index,
                                position: destination?.index,
                                status: destination?.droppableId,
                            }
                        )
                        const result = await dispatch(
                            mainTaskUpdate({ id: cardId, body })
                        )
                        data = result?.payload?.data
                    } else {
                        const result = await dispatch(
                            taskUpdateSupport({
                                _id: cardId,
                                updateTask: {
                                    initialPosition: source?.index,
                                    position: destination?.index,
                                    projectId: projectId,
                                    status: destination?.droppableId,
                                },
                            })
                        )
                        data = result?.payload?.data
                    }

                    const success = isMain
                        ? isTaskMutationSuccess(data)
                        : data?.success

                    if (!success) {
                        showActionNotification({
                            type: 'error',
                            message:
                                data?.message ||
                                (source.droppableId === destination.droppableId
                                    ? 'Failed to update task position'
                                    : 'Failed to move task'),
                        })
                        refreshTasks(0)
                    }
                } catch (error) {
                    showActionNotification({
                        type: 'error',
                        message:
                            source.droppableId === destination.droppableId
                                ? 'Failed to update task position'
                                : 'Failed to move task',
                    })
                    refreshTasks(0)
                } finally {
                    pendingCardReordersRef.current = Math.max(
                        0,
                        pendingCardReordersRef.current - 1
                    )
                }
            }

            void persistReorder()
        },
        [dispatch, projectId, isMain, refreshTasks]
    )

    // Handle drag end
    const onDragEnd = useCallback(
        (draggable) => {
            const { draggableId } = draggable

            if (!canEditModule) {
                return
            }

            if (!draggable?.destination) {
                return
            }

            const source = draggable?.source
            const destination = draggable?.destination

            if (source !== null && destination !== null) {
                if (
                    source?.droppableId === destination?.droppableId &&
                    source?.index === destination?.index
                ) {
                    return
                }

                if (draggable?.type === 'cards') {
                    // Reordering swimlanes
                    orderColumns(source?.index, destination?.index)
                } else if (draggable?.type === 'column') {
                    // Moving tasks
                    orderCards(source, destination, draggableId)
                }
            }
        },
        [orderColumns, orderCards, canEditModule]
    )

    const initialCreateTask = React.useMemo(
        () => ({
            open: false,
            text: '',
            assigneeId: null,
            assigneeData: null,
            loading: false,
            _id: null,
            position: 'bottom', // 'top' or 'bottom'
        }),
        []
    )

    const handleCreateTaskClick = (groupId) => {
        if (!canEditModule) return
        setCreateTaskStates((prev) => ({
            ...prev,
            [groupId]: {
                ...initialCreateTask,
                open: true,
                _id: groupId,
                position: 'bottom',
            },
        }))
    }

    const handleCreateTaskClickFromHeader = useCallback(
        (groupId) => {
            if (!canEditModule) return
            setCreateTaskStates((prev) => ({
                ...prev,
                [groupId]: {
                    ...initialCreateTask,
                    open: true,
                    _id: groupId,
                    position: 'top',
                },
            }))
        },
        [initialCreateTask, canEditModule]
    )

    // Expose function to add task to first swimlane
    useEffect(() => {
        if (onAddTaskToFirstSwimlane) {
            onAddTaskToFirstSwimlane.current = () => {
                if (!canEditModule) return
                const firstSwimlane = (localTaskList || projectTaskList)?.[0]
                if (firstSwimlane?._id) {
                    handleCreateTaskClickFromHeader(firstSwimlane._id)
                }
            }
        }
    }, [
        localTaskList,
        projectTaskList,
        onAddTaskToFirstSwimlane,
        handleCreateTaskClickFromHeader,
        canEditModule,
    ])

    const setCreateTask = useCallback(
        (groupId) => (newState) => {
            setCreateTaskStates((prev) => {
                const prevState = prev[groupId] || initialCreateTask
                const resolved =
                    typeof newState === 'function'
                        ? newState(prevState)
                        : newState
                return {
                    ...prev,
                    [groupId]: resolved,
                }
            })
        },
        [initialCreateTask]
    )

    const createTaskInFlightRef = useRef({})

    const normalizeAssigneeForTaskCard = (assigneeId, assigneeData) => {
        if (assigneeData && typeof assigneeData === 'object') {
            return [assigneeData]
        }
        if (assigneeId) {
            return [{ _id: assigneeId }]
        }
        return null
    }

    const buildPendingBoardTask = ({
        tempId,
        title,
        groupId,
        assigneeId,
        assigneeData,
        position,
    }) => ({
        _id: tempId,
        title,
        status: groupId,
        position,
        type: 'Task',
        assigneeId: normalizeAssigneeForTaskCard(assigneeId, assigneeData),
        pendingCreate: true,
    })

    const extractCreatedTaskFromResponse = (createdData) => {
        if (!createdData) return null
        const candidates = [
            createdData?.data?.task,
            createdData?.data,
            createdData?.task,
            createdData,
        ]
        for (const item of candidates) {
            if (!item || typeof item !== 'object') continue
            const id = item._id || item.id
            if (!id) continue
            return {
                ...item,
                _id: id,
                title: item.title ?? item.name ?? '',
            }
        }
        return null
    }

    const insertTaskToGroup = (groups, groupId, task, placement = 'bottom') =>
        groups.map((group) => {
            if (group?._id !== groupId || !Array.isArray(group?.list)) {
                return group
            }
            const list =
                placement === 'top'
                    ? [task, ...group.list]
                    : [...group.list, task]
            return { ...group, list }
        })

    const replaceTaskInGroup = (groups, groupId, tempId, nextTask) =>
        groups.map((group) => {
            if (group?._id !== groupId || !Array.isArray(group?.list)) {
                return group
            }
            const idx = group.list.findIndex((t) => t._id === tempId)
            if (idx < 0) {
                return { ...group, list: [...group.list, nextTask] }
            }
            const list = group.list.map((t, i) =>
                i === idx ? { ...nextTask, pendingCreate: false } : t
            )
            return { ...group, list }
        })

    const removeTaskFromGroup = (groups, groupId, taskId) =>
        groups.map((group) =>
            group?._id === groupId && Array.isArray(group?.list)
                ? {
                      ...group,
                      list: group.list.filter((t) => t._id !== taskId),
                  }
                : group
        )

    const createTaskFromBoard = async (taskName, groupId, options = {}) => {
        if (!canEditModule) return
        if (createTaskInFlightRef.current[groupId]) {
            return
        }

        const createTaskState = createTaskStates[groupId]
        const assigneeId =
            options.assigneeId ??
            createTaskState?.assigneeId ??
            createTaskState?.assigneeData?._id ??
            null
        const assigneeData =
            options.assigneeData ?? createTaskState?.assigneeData ?? null
        const trimmedTitle = String(taskName || '').trim()
        if (!trimmedTitle) {
            return
        }

        createTaskInFlightRef.current[groupId] = true
        const pendingTaskId = createPendingTaskId()

        setCreateTaskStates((prev) => ({
            ...prev,
            [groupId]: initialCreateTask,
        }))

        const taskPlacement =
            options.position ?? createTaskState?.position ?? 'bottom'
        const isTopPlacement = taskPlacement === 'top'

        setLocalTaskList((prev) => {
            const groups = prev || []
            const group = groups.find((g) => g?._id === groupId)
            const listLength = group?.list?.length ?? 0
            const boardPosition = isTopPlacement ? 0 : listLength
            const pendingTask = buildPendingBoardTask({
                tempId: pendingTaskId,
                title: trimmedTitle,
                groupId,
                assigneeId,
                assigneeData,
                position: boardPosition,
            })
            return insertTaskToGroup(
                groups,
                groupId,
                pendingTask,
                taskPlacement
            )
        })

        const payload = isMain
            ? {
                  title: trimmedTitle,
                  projectId: projectId,
                  description: '',
                  type: options.type ?? 'Task',
                  status: groupId || 'To Do',
                  priority: 'Medium',
                  assigneeId,
                  start: createTaskState?.start || null,
                  end: createTaskState?.end || null,
                  ...(isTopPlacement ? { placement: 'top' } : {}),
              }
            : {
                  title: trimmedTitle,
                  projectId: projectId,
                  assigneeId,
                  type: options.type ?? 'Task',
                  status: groupId || 'To Do',
                  ...(isTopPlacement ? { placement: 'top' } : {}),
              }

        try {
            const result = await dispatch(
                isMain ? mainTaskCreate(payload) : taskCreateSupport(payload)
            )

            const createdData = result?.payload?.data
            const createOk = isMain
                ? isTaskMutationSuccess(createdData)
                : createdData?.success

            if (createOk) {
                const createdTask = extractCreatedTaskFromResponse(createdData)
                if (createdTask) {
                    setLocalTaskList((prev) =>
                        replaceTaskInGroup(
                            prev || [],
                            groupId,
                            pendingTaskId,
                            createdTask
                        )
                    )
                }
                // If API omits task body, keep pending row until list refresh replaces local state

                showActionNotification({
                    message: 'Task created successfully',
                    subMessage: trimmedTitle,
                    actionButtonText: 'Undo',
                    notificationKey: 'task-add',
                })

                refreshTasks(0)
            } else {
                setLocalTaskList((prev) =>
                    removeTaskFromGroup(prev || [], groupId, pendingTaskId)
                )
                setCreateTaskStates((prev) => ({
                    ...prev,
                    [groupId]: {
                        open: true,
                        text: trimmedTitle,
                        assigneeId,
                        assigneeData,
                        loading: false,
                        _id: groupId,
                        position: createTaskState?.position || 'bottom',
                    },
                }))
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Could not create task',
                })
            }
        } catch (error) {
            setLocalTaskList((prev) =>
                removeTaskFromGroup(prev || [], groupId, pendingTaskId)
            )
            setCreateTaskStates((prev) => ({
                ...prev,
                [groupId]: {
                    open: true,
                    text: trimmedTitle,
                    assigneeId,
                    assigneeData,
                    loading: false,
                    _id: groupId,
                    position: createTaskState?.position || 'bottom',
                },
            }))
            showActionNotification({
                type: 'error',
                message: 'Failed to create task',
            })
        } finally {
            delete createTaskInFlightRef.current[groupId]
        }
    }

    const renderContent = () => {
        if (
            projectTaskListStatus === 'loading' &&
            projectTaskList?.length === 0
        ) {
            return (
                <div
                    className={styles.loadingContainer}
                    style={{
                        width: '100%',
                    }}
                >
                    <Icon
                        icon="line-md:loading-loop"
                        style={{ fontSize: 48, color: '#6366f1' }}
                    />
                </div>
            )
        }

        if (
            projectTaskList?.length === 0 &&
            projectTaskListStatus === 'loaded'
        ) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIconBadge} aria-hidden>
                        <Icon
                            icon="mdi:clipboard-text-outline"
                            width={22}
                            height={22}
                            className={styles.emptyIcon}
                        />
                    </div>
                    <Text className={styles.emptyText}>No tasks yet</Text>
                    <Text className={styles.emptySubText}>
                        Create your first task to get started.
                    </Text>
                </div>
            )
        }

        if (Array.isArray(projectTaskList) && projectTaskList?.length > 0) {
            const isGrouped = projectTaskList[0]?.list

            if (isGrouped) {
                return (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div
                            className={styles.kanbanBoardContainer}
                            style={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                scrollbarColor: '#a0a1a1 #fffbfb',
                                flex: '1 1 auto',
                                minHeight: 0,
                            }}
                        >
                            <Droppable
                                droppableId="swimlanes"
                                type="cards"
                                direction="horizontal"
                            >
                                {(provided) => (
                                    <Row
                                        wrap={false}
                                        gutter={14}
                                        className={styles.kanbanBoardRow}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {(localTaskList || projectTaskList).map(
                                            (group, index) => {
                                                const countTask =
                                                    group?.list?.filter(
                                                        (item) => item !== null
                                                    )?.length || 0
                                                const sectionName =
                                                    group?._id || 'Unassigned'
                                                const sectionNameMaxLength =
                                                    hoveredSectionId ===
                                                    group?._id
                                                        ? SECTION_NAME_MAX_DISPLAY_ON_HOVER
                                                        : SECTION_NAME_MAX_DISPLAY
                                                const displaySectionName =
                                                    formatSectionNameDisplay(
                                                        sectionName,
                                                        sectionNameMaxLength
                                                    )
                                                const isSectionNameTruncated =
                                                    displaySectionName !==
                                                    sectionName
                                                const showColumnHeaderActions =
                                                    hoveredSectionId ===
                                                        group?._id ||
                                                    Boolean(
                                                        sectionMenuOpen[
                                                            group?._id
                                                        ]
                                                    )

                                                return (
                                                    <Draggable
                                                        key={group?._id}
                                                        draggableId={group?._id}
                                                        index={
                                                            group?.position ??
                                                            index
                                                        }
                                                        isDragDisabled={
                                                            !canEditModule
                                                        }
                                                    >
                                                        {(provided) => (
                                                            <Col
                                                                ref={
                                                                    provided.innerRef
                                                                }
                                                                {...provided.draggableProps}
                                                                className={
                                                                    styles.kanbanColumn
                                                                }
                                                                style={{
                                                                    ...provided
                                                                        .draggableProps
                                                                        .style,
                                                                }}
                                                            >
                                                                <Row
                                                                    align={
                                                                        'middle'
                                                                    }
                                                                    justify={
                                                                        'space-between'
                                                                    }
                                                                    className={
                                                                        styles.columnHeader
                                                                    }
                                                                    style={{
                                                                        position:
                                                                            'relative',
                                                                        padding:
                                                                            editingSectionId ===
                                                                            group?._id
                                                                                ? '1.9px'
                                                                                : '8px',
                                                                    }}
                                                                    onMouseEnter={() =>
                                                                        setHoveredSectionId(
                                                                            group?._id
                                                                        )
                                                                    }
                                                                    onMouseLeave={() =>
                                                                        setHoveredSectionId(
                                                                            null
                                                                        )
                                                                    }
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <div
                                                                        className={
                                                                            styles.columnTitle
                                                                        }
                                                                    >
                                                                        {editingSectionId ===
                                                                        group?._id ? (
                                                                            <Input
                                                                                ref={
                                                                                    sectionNameInputRef
                                                                                }
                                                                                value={
                                                                                    editingSectionName
                                                                                }
                                                                                onChange={(
                                                                                    e
                                                                                ) =>
                                                                                    setEditingSectionName(
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                                onBlur={() =>
                                                                                    handleSectionNameBlur(
                                                                                        group?._id
                                                                                    )
                                                                                }
                                                                                onKeyDown={(
                                                                                    e
                                                                                ) =>
                                                                                    handleSectionNameKeyDown(
                                                                                        group?._id,
                                                                                        e
                                                                                    )
                                                                                }
                                                                                className={
                                                                                    styles.sectionNameInput
                                                                                }
                                                                                autoFocus
                                                                                onClick={(
                                                                                    e
                                                                                ) =>
                                                                                    e.stopPropagation()
                                                                                }
                                                                            />
                                                                        ) : isSectionNameTruncated ? (
                                                                            <Tooltip
                                                                                title={
                                                                                    sectionName
                                                                                }
                                                                            >
                                                                                <span
                                                                                    className={
                                                                                        styles.columnSectionName
                                                                                    }
                                                                                    style={{
                                                                                        fontWeight: 600,
                                                                                        cursor: 'text',
                                                                                    }}
                                                                                    onClick={(
                                                                                        e
                                                                                    ) => {
                                                                                        handleSectionNameClick(
                                                                                            group?._id,
                                                                                            e
                                                                                        )
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        displaySectionName
                                                                                    }
                                                                                </span>
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <span
                                                                                className={
                                                                                    styles.columnSectionName
                                                                                }
                                                                                style={{
                                                                                    fontWeight: 600,
                                                                                    cursor: 'text',
                                                                                }}
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    handleSectionNameClick(
                                                                                        group?._id,
                                                                                        e
                                                                                    )
                                                                                }}
                                                                            >
                                                                                {
                                                                                    displaySectionName
                                                                                }
                                                                            </span>
                                                                        )}
                                                                        <span
                                                                            className={
                                                                                styles.columnCount
                                                                            }
                                                                            style={{
                                                                                backgroundColor:
                                                                                    getColumnColor(
                                                                                        sectionName
                                                                                    ),
                                                                            }}
                                                                        >
                                                                            {' '}
                                                                            {
                                                                                countTask
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    {showColumnHeaderActions &&
                                                                    canEditModule ? (
                                                                        <div
                                                                            className={
                                                                                styles.columnHeaderActions
                                                                            }
                                                                        >
                                                                            <Tooltip title="Add Task">
                                                                                <Icon
                                                                                    icon="mdi:plus"
                                                                                    width={
                                                                                        20
                                                                                    }
                                                                                    height={
                                                                                        20
                                                                                    }
                                                                                    className={
                                                                                        styles.columnHeaderPlusIcon
                                                                                    }
                                                                                    onClick={(
                                                                                        e
                                                                                    ) => {
                                                                                        e.stopPropagation()
                                                                                        handleCreateTaskClickFromHeader(
                                                                                            group?._id
                                                                                        )
                                                                                    }}
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        color: '#636363',
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                            <Popover
                                                                                content={renderSectionMenu(
                                                                                    group?._id
                                                                                )}
                                                                                trigger="click"
                                                                                open={
                                                                                    sectionMenuOpen[
                                                                                        group
                                                                                            ?._id
                                                                                    ]
                                                                                }
                                                                                onOpenChange={(
                                                                                    open
                                                                                ) =>
                                                                                    handleSectionMenuOpenChange(
                                                                                        group?._id,
                                                                                        open
                                                                                    )
                                                                                }
                                                                                placement="bottomRight"
                                                                                classNames={{
                                                                                    root: styles.sectionMenuPopover,
                                                                                }}
                                                                            >
                                                                                <Tooltip title="More options">
                                                                                    <Icon
                                                                                        icon="weui:more-outlined"
                                                                                        width={
                                                                                            20
                                                                                        }
                                                                                        height={
                                                                                            20
                                                                                        }
                                                                                        onClick={(
                                                                                            e
                                                                                        ) => {
                                                                                            e.stopPropagation()
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            color: '#636363',
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>
                                                                            </Popover>
                                                                        </div>
                                                                    ) : null}
                                                                </Row>

                                                                <Droppable
                                                                    droppableId={
                                                                        group?._id
                                                                    }
                                                                    type="column"
                                                                    direction="vertical"
                                                                >
                                                                    {(
                                                                        provided
                                                                    ) => (
                                                                        <div
                                                                            ref={
                                                                                provided.innerRef
                                                                            }
                                                                            {...provided.droppableProps}
                                                                            className={
                                                                                styles.columnContent
                                                                            }
                                                                            id={`swimlane-${group?._id}`}
                                                                            style={{
                                                                                maxHeight: `calc(100vh - 264px)`,
                                                                                overflowY:
                                                                                    'auto',
                                                                                scrollbarColor:
                                                                                    '#a0a1a1 #fffbfb',
                                                                                scrollbarWidth:
                                                                                    'thin',
                                                                            }}
                                                                        >
                                                                            {createTaskStates[
                                                                                group
                                                                                    ?._id
                                                                            ]
                                                                                ?.open &&
                                                                                createTaskStates[
                                                                                    group
                                                                                        ?._id
                                                                                ]
                                                                                    ?.position ===
                                                                                    'top' && (
                                                                                    <CreateTaskCard
                                                                                        createTask={
                                                                                            createTaskStates[
                                                                                                group
                                                                                                    ?._id
                                                                                            ]
                                                                                        }
                                                                                        setCreateTask={setCreateTask(
                                                                                            group?._id
                                                                                        )}
                                                                                        filteredAssignees={
                                                                                            filteredAssignees
                                                                                        }
                                                                                        createTaskFromBoard={(
                                                                                            taskName,
                                                                                            options
                                                                                        ) =>
                                                                                            createTaskFromBoard(
                                                                                                taskName,
                                                                                                group?._id,
                                                                                                options
                                                                                            )
                                                                                        }
                                                                                        initialCreateTask={
                                                                                            initialCreateTask
                                                                                        }
                                                                                        projectId={
                                                                                            projectId
                                                                                        }
                                                                                        status={
                                                                                            group?._id
                                                                                        }
                                                                                        position={
                                                                                            'top'
                                                                                        }
                                                                                        taskListKind={
                                                                                            taskListKind
                                                                                        }
                                                                                    />
                                                                                )}

                                                                            {group?.list &&
                                                                                group
                                                                                    ?.list
                                                                                    .length >
                                                                                    0 &&
                                                                                group?.list.map(
                                                                                    (
                                                                                        task,
                                                                                        taskIndex
                                                                                    ) => {
                                                                                        if (
                                                                                            task
                                                                                                ?.parentTask
                                                                                                ?.length >
                                                                                            0
                                                                                        ) {
                                                                                            return null
                                                                                        }
                                                                                        return (
                                                                                            <Draggable
                                                                                                key={
                                                                                                    task._id ||
                                                                                                    taskIndex
                                                                                                }
                                                                                                draggableId={
                                                                                                    task._id
                                                                                                }
                                                                                                index={
                                                                                                    task.position ??
                                                                                                    taskIndex
                                                                                                }
                                                                                isDragDisabled={
                                                                                    !canEditModule ||
                                                                                    isPendingCreateTask(
                                                                                        task
                                                                                    )
                                                                                }
                                                                                            >
                                                                                                {(
                                                                                                    provided
                                                                                                ) => (
                                                                                                    <div
                                                                                                        ref={
                                                                                                            provided.innerRef
                                                                                                        }
                                                                                                        {...provided.draggableProps}
                                                                                                        {...(isPendingCreateTask(
                                                                                                            task
                                                                                                        )
                                                                                                            ? {}
                                                                                                            : provided.dragHandleProps)}
                                                                                                    >
                                                                                                        <TaskCard
                                                                                                            task={
                                                                                                                task
                                                                                                            }
                                                                                                            token={
                                                                                                                token
                                                                                                            }
                                                                                                            currentProjectData={
                                                                                                                currentProjectData
                                                                                                            }
                                                                                                            onTaskClick={
                                                                                                                onTaskClick
                                                                                                            }
                                                                                                            projectId={
                                                                                                                projectId
                                                                                                            }
                                                                                                            dispatch={
                                                                                                                dispatch
                                                                                                            }
                                                                                                            filteredAssignees={
                                                                                                                filteredAssignees
                                                                                                            }
                                                                                                            setLocalTaskList={
                                                                                                                setLocalTaskList
                                                                                                            }
                                                                                                            taskListKind={
                                                                                                                taskListKind
                                                                                                            }
                                                                                                            refreshTasks={
                                                                                                                refreshTasks
                                                                                                            }
                                                                                                            taskRouteView={
                                                                                                                taskRouteView
                                                                                                            }
                                                                                                            listQuery={
                                                                                                                listQuery
                                                                                                            }
                                                                                                        />
                                                                                                    </div>
                                                                                                )}
                                                                                            </Draggable>
                                                                                        )
                                                                                    }
                                                                                )}

                                                                            {createTaskStates[
                                                                                group
                                                                                    ?._id
                                                                            ]
                                                                                ?.open &&
                                                                            createTaskStates[
                                                                                group
                                                                                    ?._id
                                                                            ]
                                                                                ?.position ===
                                                                                'bottom' ? (
                                                                                <CreateTaskCard
                                                                                    createTask={
                                                                                        createTaskStates[
                                                                                            group
                                                                                                ?._id
                                                                                        ]
                                                                                    }
                                                                                    setCreateTask={setCreateTask(
                                                                                        group?._id
                                                                                    )}
                                                                                    filteredAssignees={
                                                                                        filteredAssignees
                                                                                    }
                                                                                    createTaskFromBoard={(
                                                                                        taskName,
                                                                                        options
                                                                                    ) =>
                                                                                        createTaskFromBoard(
                                                                                            taskName,
                                                                                            group?._id,
                                                                                            options
                                                                                        )
                                                                                    }
                                                                                    initialCreateTask={
                                                                                        initialCreateTask
                                                                                    }
                                                                                    projectId={
                                                                                        projectId
                                                                                    }
                                                                                    status={
                                                                                        group?._id
                                                                                    }
                                                                                    taskListKind={
                                                                                        taskListKind
                                                                                    }
                                                                                />
                                                                            ) : null}
                                                                            {
                                                                                provided.placeholder
                                                                            }
                                                                            {canEditModule &&
                                                                                !createTaskStates[
                                                                                    group
                                                                                        ?._id
                                                                                ]
                                                                                    ?.open && (
                                                                                <div
                                                                                    style={{
                                                                                        marginTop:
                                                                                            '8px',
                                                                                    }}
                                                                                >
                                                                                    <Button
                                                                                        type="text"
                                                                                        className={
                                                                                            styles.createTaskButton
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleCreateTaskClick(
                                                                                                group?._id
                                                                                            )
                                                                                        }
                                                                                        icon={
                                                                                            <Icon
                                                                                                icon="tabler:plus"
                                                                                                width={
                                                                                                    18
                                                                                                }
                                                                                                height={
                                                                                                    18
                                                                                                }
                                                                                            />
                                                                                        }
                                                                                    >
                                                                                        Add
                                                                                        Task
                                                                                    </Button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </Droppable>
                                                            </Col>
                                                        )}
                                                    </Draggable>
                                                )
                                            }
                                        )}
                                        {canEditModule && (
                                        <Col className={styles.addSwimlaneCol}>
                                            {isAddingSection ? (
                                                <Input
                                                    ref={sectionInputRef}
                                                    value={newSectionName}
                                                    onChange={(e) =>
                                                        setNewSectionName(
                                                            e.target.value
                                                        )
                                                    }
                                                    onBlur={
                                                        handleSectionInputBlur
                                                    }
                                                    onKeyDown={
                                                        handleSectionInputKeyDown
                                                    }
                                                    placeholder="New section"
                                                    disabled={isCreatingSection}
                                                    className={
                                                        styles.newSectionInput
                                                    }
                                                    autoFocus
                                                />
                                            ) : (
                                                <Button
                                                    type="text"
                                                    className={
                                                        styles.addSectionButton
                                                    }
                                                    onClick={handleAddSection}
                                                    icon={
                                                        <Icon
                                                            icon="mdi:plus"
                                                            style={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    }
                                                >
                                                    Add section
                                                </Button>
                                            )}
                                        </Col>
                                        )}
                                        {provided.placeholder}
                                    </Row>
                                )}
                            </Droppable>
                        </div>
                    </DragDropContext>
                )
            } else {
                return (
                    <InfiniteScroll
                        dataLength={projectTaskList?.length}
                        next={fetchMoreTasks}
                        hasMore={projectTaskListIsNext}
                        loader={
                            <div className={styles.loadingMore}>
                                <Icon
                                    icon="line-md:loading-loop"
                                    style={{ fontSize: 24, color: '#6366f1' }}
                                />
                            </div>
                        }
                        scrollableTarget="project-detail-scroll"
                    >
                        <div className={styles.taskList}>
                            {projectTaskList.map((task) => (
                                <TaskCard
                                    key={task._id}
                                    task={task}
                                    token={token}
                                    currentProjectData={currentProjectData}
                                    onTaskClick={onTaskClick}
                                    projectId={projectId}
                                    dispatch={dispatch}
                                    filteredAssignees={filteredAssignees}
                                    setLocalTaskList={setLocalTaskList}
                                    taskListKind={taskListKind}
                                    refreshTasks={refreshTasks}
                                    taskRouteView={taskRouteView}
                                    listQuery={listQuery}
                                />
                            ))}
                        </div>
                    </InfiniteScroll>
                )
            }
        }

        return null
    }

    const handleModalCancel = () => {
        setDeleteModalOpen(false)
        setDeleteSectionData(null)
        setDeleteOption('keep')
    }

    return (
        <>
            {renderContent()}
            <DeleteSectionModal
                open={deleteModalOpen}
                onCancel={handleModalCancel}
                onConfirm={handleConfirmDeleteSection}
                deleteSectionData={deleteSectionData}
                deleteOption={deleteOption}
                setDeleteOption={setDeleteOption}
            />
        </>
    )
}

// Task Card Component matching the new design
export const TaskCard = ({
    setLocalTaskList,
    task,
    token,
    currentProjectData,
    onTaskClick,
    projectId,
    dispatch,
    filteredAssignees,
    taskListKind = 'support',
    refreshTasks,
    containerClassName = '',
    readOnlyActions: readOnlyActionsProp = false,
    singleLineTitle = false,
    taskRouteView = 'board',
    listQuery = null,
}) => {
    const isMainCard = taskListKind === 'main'
    const { canEdit: canEditModule, canDelete: canDeleteModule } =
        useTaskListKindPermission(taskListKind)
    const readOnlyActions = readOnlyActionsProp || !canEditModule
    const isPendingCreate = isPendingCreateTask(task)
    const isCardInteractive = !readOnlyActions && !isPendingCreate
    const taskTypeLabel = useMemo(() => {
        const normalized = String(task?.type || 'Task').trim()
        return normalized || 'Task'
    }, [task?.type])
    const isTypeDisabledForIssuesSprint = useMemo(() => {
        if (!isMainCard) return false
        const sprintLabel = task?.sprint?.title ?? task?.sprintId?.title ?? ''
        return /^issues$/i.test(String(sprintLabel).trim())
    }, [isMainCard, task?.sprint, task?.sprintId])
    const taskEpicId = useMemo(() => getTaskEpicId(task), [task])
    const taskEpicDisplayOption = useMemo(
        () => getEpicDisplayOptionFromTask(task),
        [task]
    )
    const showTaskEpic =
        isMainCard && taskRouteView !== 'sprint' && Boolean(taskEpicId)
    const showTypeInTitleRow = isMainCard && taskRouteView === 'sprint'
    const showTypeInEpicRow = isMainCard && taskRouteView !== 'sprint'
    const isTaskCompleted = Boolean(task?.isCompleted ?? task?.isComplete)
    const selectedAssigneeForDisplay = Array.isArray(task?.assigneeId)
        ? (task?.assigneeId?.[0] ?? null)
        : (task?.assigneeId ?? null)

    const taskDeepLink = useMemo(() => {
        if (!projectId || !task?._id) return ''
        const path = PRIVATE_ROUTES.projects.projectIdViewTask(
            projectId,
            taskRouteView,
            task._id
        )
        const search =
            taskListKind === 'main'
                ? buildProjectTaskTabSearch('development')
                : ''
        return `${window.location.origin}${path}${search}`
    }, [projectId, task?._id, taskRouteView, taskListKind])

    const hasParentTask = Boolean(
        task?.parentTask &&
        (Array.isArray(task.parentTask)
            ? task.parentTask.length > 0
            : Object.keys(task.parentTask || {}).length > 0)
    )
    const canAddSubtasks = !/^bug$/i.test(String(taskTypeLabel).trim())
    const [isEditing, setIsEditing] = useState(false)
    const [editedTitle, setEditedTitle] = useState(task.title)
    const [isUpdating, setIsUpdating] = useState(false)
    const [isTogglingComplete, setIsTogglingComplete] = useState(false)
    const [isCompleteIconHovered, setIsCompleteIconHovered] = useState(false)
    const [showCompletionCelebration, setShowCompletionCelebration] =
        useState(false)
    const [parentEpicModalOpen, setParentEpicModalOpen] = useState(false)
    const inputRef = useRef(null)
    const subTaskAccordionRef = useRef(null)

    const getInitials = (name) => {
        if (!name) return 'U'
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const handleEditClick = (e) => {
        e.stopPropagation()
        if (isPendingCreate) return
        setIsEditing(true)
        setEditedTitle(task.title)
        setTimeout(() => {
            if (inputRef.current) {
                const textArea =
                    inputRef.current.resizableTextArea?.textArea ||
                    inputRef.current
                textArea.focus()
                if (textArea.select) {
                    textArea.select()
                } else if (textArea.setSelectionRange) {
                    textArea.setSelectionRange(0, textArea.value.length)
                }
            }
        }, 0)
    }

    const handleSave = async () => {
        if (isPendingCreate) return

        if (editedTitle.trim() === task.title.trim()) {
            setIsEditing(false)
            return
        }

        if (!editedTitle.trim()) {
            showActionNotification({
                type: 'error',
                message: 'Task title cannot be empty',
            })
            setEditedTitle(task.title)
            setIsEditing(false)
            return
        }

        setIsUpdating(true)
        try {
            let result
            if (isMainCard) {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    title: editedTitle.trim(),
                })
                result = await dispatch(mainTaskUpdate({ id: task._id, body }))
            } else {
                const payload = {
                    _id: task._id,
                    updateTask: {
                        title: editedTitle.trim(),
                        projectId: projectId,
                        type: task.type || 'Task',
                        assigneeId: resolveReferenceId(task.assigneeId),
                        priority: task.priority || 'Low',
                        status: task.status || 'To Do',
                    },
                }
                result = await dispatch(taskUpdateSupport(payload))
            }

            const data = result?.payload?.data
            if (isMainCard ? isTaskMutationSuccess(data) : data?.success) {
                refreshTasks(0)
                setIsEditing(false)
            } else {
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to update task',
                })
                setEditedTitle(task.title)
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to update task',
            })
            setEditedTitle(task.title)
        } finally {
            setIsUpdating(false)
            setIsEditing(false)
        }
    }

    const handleCancel = () => {
        setEditedTitle(task.title)
        setIsEditing(false)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
    }

    const handleAssigneeChange = useCallback(
        async (selected) => {
            if (isPendingCreate) return
            const newAssigneeId = selected?._id || selected
            const currentAssigneeId = resolveReferenceId(task.assigneeId)
            if (newAssigneeId === currentAssigneeId) {
                return
            }

            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? {
                                            ...item,
                                            assigneeId: selected
                                                ? [
                                                      {
                                                          name: selected?.name,
                                                          _id: selected?._id,
                                                          email: selected?.email,
                                                          profilePicture:
                                                              selected?.profilePicture,
                                                      },
                                                  ]
                                                : null,
                                        }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                let result
                if (isMainCard) {
                    const body = buildMainTaskUpdateBodyFromTask(
                        task,
                        projectId,
                        { assigneeId: newAssigneeId }
                    )
                    result = await dispatch(
                        mainTaskUpdate({ id: task._id, body })
                    )
                } else {
                    const payload = {
                        _id: task._id,
                        updateTask: {
                            title: task.title,
                            projectId: projectId,
                            type: task.type || 'Task',
                            assigneeId: newAssigneeId,
                            priority: task.priority || 'Low',
                            status: task.status || 'To Do',
                            description: task.description,
                            start: task.start,
                            estimation: task.estimation,
                        },
                    }
                    result = await dispatch(taskUpdateSupport(payload))
                }

                const data = result?.payload?.data
                if (isMainCard ? isTaskMutationSuccess(data) : data?.success) {
                    showActionNotification({
                        type: 'success',
                        message: 'Assignee updated successfully',
                        notificationKey: 'task-assignee-updated',
                    })

                    dispatch(
                        projectTaskListAssigneeUser({
                            skip: 0,
                            limit: 100,
                            search: '',
                            projectId: projectId,
                            ...(isMainCard ? { type: 'pms' } : {}),
                        })
                    )
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update assignee',
                    })
                }
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update assignee',
                })
            }
        },
        [task, projectId, dispatch, setLocalTaskList, isMainCard, refreshTasks]
    )

    const handleDateRangeChange = useCallback(
        async (dateRange) => {
            if (isPendingCreate) return
            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? {
                                            ...item,
                                            start: dateRange?.start || null,
                                            end: dateRange?.end || null,
                                        }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                let result
                if (isMainCard) {
                    const body = buildMainTaskUpdateBodyFromTask(
                        task,
                        projectId,
                        {
                            start: dateRange?.start || null,
                            end: dateRange?.end || null,
                        }
                    )
                    result = await dispatch(
                        mainTaskUpdate({ id: task._id, body })
                    )
                } else {
                    const payload = {
                        _id: task._id,
                        updateTask: {
                            title: task.title,
                            projectId: projectId,
                            type: task.type || 'Task',
                            assigneeId:
                                task.assigneeId?.[0]?._id ??
                                task.assigneeId?._id ??
                                task.assigneeId ??
                                null,
                            priority: task.priority || 'Low',
                            status: task.status || 'To Do',
                            description: task.description,
                            start: dateRange?.start || null,
                            end: dateRange?.end || null,
                            estimation: task.estimation,
                        },
                    }
                    result = await dispatch(taskUpdateSupport(payload))
                }

                const data = result?.payload?.data
                if (isMainCard ? isTaskMutationSuccess(data) : data?.success) {
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update date',
                    })
                }
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update date',
                })
            }
        },
        [task, projectId, dispatch, setLocalTaskList, isMainCard, refreshTasks]
    )

    const handleTypeChange = useCallback(
        async (_name, nextType) => {
            if (!isMainCard || readOnlyActions || isPendingCreate) return
            const typeValue = nextType || 'Task'
            if (
                String(typeValue).toLowerCase() ===
                String(taskTypeLabel).toLowerCase()
            ) {
                return
            }

            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? { ...item, type: typeValue }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    type: typeValue,
                })
                const result = await dispatch(
                    mainTaskUpdate({ id: task._id, body })
                )
                const data = result?.payload?.data
                if (isTaskMutationSuccess(data)) {
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update type',
                    })
                }
            } catch {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update type',
                })
            }
        },
        [
            task,
            projectId,
            dispatch,
            setLocalTaskList,
            isMainCard,
            readOnlyActions,
            refreshTasks,
            taskTypeLabel,
        ]
    )

    const handleEpicChange = useCallback(
        async (epicId) => {
            if (!isMainCard || isPendingCreate) return false
            const currentEpicId = getTaskEpicId(task)
            if (String(epicId ?? '') === String(currentEpicId ?? '')) {
                return true
            }

            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? { ...item, epic: epicId, epicId }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    epic: epicId,
                })
                const result = await dispatch(
                    mainTaskUpdate({ id: task._id, body })
                )
                const data = result?.payload?.data
                if (isTaskMutationSuccess(data)) {
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update epic',
                    })
                }
            } catch {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update epic',
                })
            }
        },
        [task, projectId, dispatch, setLocalTaskList, isMainCard, refreshTasks]
    )

    const performSupportCompleteToggle = useCallback(async () => {
        if (
            isMainCard ||
            isTogglingComplete ||
            isPendingCreate ||
            readOnlyActions
        )
            return

        const nextCompleted = !Boolean(task?.isCompleted ?? task?.isComplete)
        setIsTogglingComplete(true)
        setLocalTaskList((prev) =>
            prev.map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === task?._id
                                  ? { ...item, isCompleted: nextCompleted }
                                  : item
                          ),
                      }
                    : group
            )
        )
        try {
            const payload = {
                _id: task._id,
                updateTask: {
                    title: task.title,
                    projectId: projectId,
                    type: task.type || 'Task',
                    assigneeId:
                        task.assigneeId?.[0]?._id ??
                        task.assigneeId?._id ??
                        task.assigneeId ??
                        null,
                    priority: task.priority || 'Low',
                    status: task.status || 'To Do',
                    description: task.description,
                    start: task.start,
                    end: task.end,
                    estimation: task.estimation,
                    isCompleted: nextCompleted,
                },
            }

            const result = await dispatch(taskUpdateSupport(payload))
            const data = result?.payload?.data
            if (data?.success) {
                if (nextCompleted) {
                    setShowCompletionCelebration(true)
                }
                refreshTasks(0)
            } else {
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to update task completion',
                })
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to update task completion',
            })
        } finally {
            setIsTogglingComplete(false)
        }
    }, [
        task,
        projectId,
        dispatch,
        isMainCard,
        isTogglingComplete,
        isPendingCreate,
        readOnlyActions,
        refreshTasks,
        setLocalTaskList,
    ])

    const handleToggleSupportTaskComplete = useCallback(
        (e) => {
            e.stopPropagation()
            performSupportCompleteToggle()
        },
        [performSupportCompleteToggle]
    )

    const handleOpenTaskDetails = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (isPendingCreate) return
            onTaskClick(task._id)
        },
        [isPendingCreate, onTaskClick, task._id]
    )

    const handleOpenTaskInNewTab = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (isPendingCreate || !taskDeepLink) return
            window.open(taskDeepLink, '_blank', 'noopener,noreferrer')
        },
        [isPendingCreate, taskDeepLink]
    )

    const handleCopyTaskLink = useCallback(
        async (e) => {
            e?.stopPropagation?.()
            if (isPendingCreate || !taskDeepLink) return
            const ok = await copyTaskContextLink(taskDeepLink)
            showActionNotification({
                type: ok ? 'success' : 'error',
                message: ok ? 'Task link copied' : 'Could not copy link',
                notificationKey: 'task-card-link-copied',
            })
        },
        [isPendingCreate, taskDeepLink]
    )

    const handleAddSubtaskFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (
                hasParentTask ||
                !canAddSubtasks ||
                readOnlyActions ||
                isPendingCreate
            )
                return
            if (
                !isMainCard &&
                subTaskAccordionRef.current?.openAddSubtaskComposer
            ) {
                subTaskAccordionRef.current.openAddSubtaskComposer()
                return
            }
            onTaskClick(task._id, 'add-subtasks')
        },
        [
            hasParentTask,
            canAddSubtasks,
            readOnlyActions,
            isPendingCreate,
            isMainCard,
            onTaskClick,
            task._id,
        ]
    )

    const handleParentEpicFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (readOnlyActions || !isMainCard || hasParentTask) return
            setParentEpicModalOpen(true)
        },
        [readOnlyActions, isMainCard, hasParentTask]
    )

    const handleParentEpicModalConfirm = useCallback(
        (epicId) => handleEpicChange(epicId),
        [handleEpicChange]
    )

    const handleDeleteTaskFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (!canDeleteModule || isPendingCreate || !task?._id) return
            confirmDeleteTask({
                type: task?.type,
                onConfirm: async () => {
                    try {
                        const result = await dispatch(
                            isMainCard
                                ? mainTaskDelete(task._id)
                                : taskDeleteSupport(task._id)
                        )
                        const data = result?.payload?.data
                        const okDelete = isMainCard
                            ? isTaskMutationSuccess(data)
                            : data?.success
                        if (okDelete) {
                            showActionNotification({
                                message:
                                    data?.message ||
                                    'Task deleted successfully',
                                subMessage: task.title,
                                notificationKey: 'task-deleted-from-board',
                            })
                            refreshTasks(0)
                        } else {
                            showActionNotification({
                                type: 'error',
                                message:
                                    data?.message || 'Could not delete task',
                                notificationKey: 'task-delete-failed-board',
                            })
                        }
                    } catch {
                        showActionNotification({
                            type: 'error',
                            message: 'Could not delete task',
                            notificationKey: 'task-delete-failed-board',
                        })
                    }
                },
            })
        },
        [
            canDeleteModule,
            isPendingCreate,
            task._id,
            task.title,
            task?.type,
            dispatch,
            isMainCard,
            refreshTasks,
        ]
    )

    const taskTypeSelectorNode = isMainCard ? (
        <div
            data-ignore-click
            className={styles.taskCardActionItem}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <TypeSelector
                value={taskTypeLabel}
                placeholder="Select type"
                typeOptions={TASK_TYPE_OPTIONS}
                name="type"
                compact
                disabled={
                    readOnlyActions ||
                    isPendingCreate ||
                    isTypeDisabledForIssuesSprint
                }
                updateDetails={handleTypeChange}
            />
        </div>
    ) : null

    return (
        <>
            <Dropdown
                trigger={isPendingCreate ? [] : ['contextMenu']}
                placement="bottomLeft"
                overlayClassName={styles.taskContextDropdown}
                popupRender={() => (
                    <div className={styles.taskContextMenu}>
                        {((!hasParentTask && canAddSubtasks) || !isMainCard) &&
                            !readOnlyActions && (
                                <div className={styles.taskContextMenuSection}>
                                    {!isMainCard && (
                                        <button
                                            type="button"
                                            className={
                                                styles.taskContextMenuItem
                                            }
                                            onClick={(ev) => {
                                                ev.stopPropagation()
                                                performSupportCompleteToggle()
                                            }}
                                            disabled={isTogglingComplete}
                                        >
                                            <Icon
                                                icon={
                                                    'mdi:check-circle-outline'
                                                }
                                                width={16}
                                                height={16}
                                                style={{
                                                    color: '#757677',
                                                    marginRight: 8,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span>
                                                {isTaskCompleted
                                                    ? 'Mark incomplete'
                                                    : 'Mark complete'}
                                            </span>
                                        </button>
                                    )}
                                    {!hasParentTask && canAddSubtasks && (
                                        <button
                                            type="button"
                                            className={
                                                styles.taskContextMenuItem
                                            }
                                            onClick={handleAddSubtaskFromMenu}
                                        >
                                            <Icon
                                                icon="cuida:subtask-outline"
                                                width={16}
                                                height={16}
                                                style={{
                                                    color: '#757677',
                                                    marginRight: 8,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span>Add subtask</span>
                                        </button>
                                    )}
                                    {/* {isMainCard && !hasParentTask && (
                                <button
                                    type="button"
                                    className={styles.taskContextMenuItem}
                                    onClick={handleParentEpicFromMenu}
                                >
                                    <Icon
                                        icon={
                                            taskEpicId
                                                ? 'mdi:swap-horizontal'
                                                : 'mdi:folder-plus-outline'
                                        }
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#757677',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>
                                        {taskEpicId
                                            ? 'Change parent'
                                            : 'Add parent'}
                                    </span>
                                </button>
                            )} */}
                                </div>
                            )}
                        <div className={styles.taskContextMenuSection}>
                            <button
                                type="button"
                                className={styles.taskContextMenuItem}
                                onClick={handleOpenTaskDetails}
                            >
                                <Icon
                                    icon="si:eye-line"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#757677',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>Open task details</span>
                            </button>
                            <button
                                type="button"
                                className={styles.taskContextMenuItem}
                                onClick={handleOpenTaskInNewTab}
                            >
                                <Icon
                                    icon="fluent:open-48-filled"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#757677',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>Open in new tab</span>
                            </button>
                            <button
                                type="button"
                                className={styles.taskContextMenuItem}
                                onClick={handleCopyTaskLink}
                            >
                                <Icon
                                    icon="mdi:link-variant"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#757677',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>Copy task link</span>
                            </button>
                        </div>

                        {canDeleteModule && (
                            <div className={styles.taskContextMenuSection}>
                                <button
                                    type="button"
                                    className={`${styles.taskContextMenuItem} ${styles.taskContextMenuItemDanger}`}
                                    onClick={handleDeleteTaskFromMenu}
                                >
                                    <Icon
                                        icon="fluent:delete-20-regular"
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#dc2626',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>{getDeleteTaskMenuLabel(task?.type)}</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            >
                <div
                    className={`${styles.taskCardNew} ${containerClassName} ${
                        isPendingCreate ? styles.taskCardPendingCreate : ''
                    }`.trim()}
                    style={{
                        opacity: isPendingCreate
                            ? 0.72
                            : isTaskCompleted
                              ? 0.7
                              : 1,
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onClick={(e) => {
                        if (isPendingCreate) return

                        const shouldIgnore =
                            e.target.closest('[data-ignore-click]') ||
                            e.target.closest('.ignore-view-task') ||
                            e.target.closest('.taskCardEditIcon') ||
                            e.target.closest('.taskCardLikeIcon') ||
                            e.target.closest('.taskCardCalendarIcon') ||
                            e.target.closest('.taskCardActionsRow') ||
                            e.target.closest('.ant-popover') ||
                            e.target.tagName === 'svg' ||
                            e.target.tagName === 'path'

                        if (!shouldIgnore) {
                            onTaskClick(task._id)
                        }
                    }}
                >
                    <Row
                        justify={'space-between'}
                        className={styles.taskCardRow}
                    >
                        <Row align={'middle'} style={{ gap: '8px', flex: 1 }}>
                            {!isMainCard && (
                                <span
                                    data-ignore-click
                                    onClick={
                                        isCardInteractive
                                            ? handleToggleSupportTaskComplete
                                            : undefined
                                    }
                                    onMouseEnter={
                                        isCardInteractive
                                            ? () =>
                                                  setIsCompleteIconHovered(true)
                                            : undefined
                                    }
                                    onMouseLeave={
                                        isCardInteractive
                                            ? () =>
                                                  setIsCompleteIconHovered(
                                                      false
                                                  )
                                            : undefined
                                    }
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        border: `1px solid ${
                                            isTaskCompleted
                                                ? 'transparent'
                                                : isCompleteIconHovered
                                                  ? '#5da182'
                                                  : '#cbd5e1'
                                        }`,
                                        backgroundColor: isTaskCompleted
                                            ? '#5da182'
                                            : isCompleteIconHovered
                                              ? '#f0fdf4'
                                              : 'transparent',
                                        cursor: !isCardInteractive
                                            ? 'default'
                                            : isTogglingComplete
                                              ? 'not-allowed'
                                              : 'pointer',
                                        opacity: isTogglingComplete ? 0.6 : 1,
                                        transition:
                                            'all 150ms ease, border-color 150ms ease, background-color 150ms ease',
                                    }}
                                >
                                    <Icon
                                        icon={'iconamoon:check-light'}
                                        width={13}
                                        height={13}
                                        style={{
                                            color: isTaskCompleted
                                                ? '#ffffff'
                                                : isCompleteIconHovered
                                                  ? '#5da182'
                                                  : '#94a3b8',
                                            transition: 'color 150ms ease',
                                        }}
                                    />
                                </span>
                            )}
                            {showTypeInTitleRow ? taskTypeSelectorNode : null}
                            {isEditing ? (
                                singleLineTitle ? (
                                    <TextArea
                                        ref={inputRef}
                                        value={editedTitle}
                                        onChange={(e) =>
                                            setEditedTitle(e.target.value)
                                        }
                                        onBlur={handleSave}
                                        onKeyDown={handleKeyDown}
                                        className={`${styles.taskCardTitleInput} ${styles.taskCardTitleInputScrollable}`}
                                        disabled={isUpdating}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        autoSize={false}
                                        rows={1}
                                        bordered={false}
                                    />
                                ) : (
                                    <TextArea
                                        ref={inputRef}
                                        value={editedTitle}
                                        onChange={(e) =>
                                            setEditedTitle(e.target.value)
                                        }
                                        onBlur={handleSave}
                                        onKeyDown={handleKeyDown}
                                        className={styles.taskCardTitleInput}
                                        disabled={isUpdating}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        autoSize={{ minRows: 1, maxRows: 5 }}
                                        bordered={false}
                                    />
                                )
                            ) : (
                                <Text
                                    className={styles.taskCardTitle}
                                    title={task.title}
                                    style={
                                        singleLineTitle
                                            ? {
                                                  display: 'block',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                              }
                                            : undefined
                                    }
                                >
                                    {task.title}
                                </Text>
                            )}
                        </Row>
                        {!isEditing && isCardInteractive && (
                            <Tooltip title="Edit" placement="top">
                                <div
                                    className={`taskCardEditIcon ${styles.taskCardEditIcon}`}
                                    data-ignore-click
                                    onClick={handleEditClick}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div className={styles.taskCardIconCircle}>
                                        <Icon
                                            icon="clarity:pencil-line"
                                            width={16}
                                            height={16}
                                            style={{ color: '#636363' }}
                                        />
                                    </div>
                                </div>
                            </Tooltip>
                        )}
                    </Row>

                    {/*
                     * Layout: flex-wrap row.
                     * - Left Row has flex:1 so it fills all leftover space and
                     *   naturally pushes the right-side items to the far edge.
                     * - Comment chip and accordionHeader sit side-by-side on the
                     *   right as plain flex siblings — NO wrapper div around them.
                     * - TaskSubTaskAccordion renders a Fragment whose second child
                     *   (accordionContent) has width:100% and wraps onto its own
                     *   flex line, spanning the full card width. Wrapping the
                     *   accordion in any container div would break that behaviour.
                     */}
                    {(showTypeInEpicRow || showTaskEpic) && (
                        <Row
                            align="middle"
                            className={styles.taskCardRow}
                            style={{ gap: '10px' }}
                        >
                            {showTypeInEpicRow ? taskTypeSelectorNode : null}
                            {showTaskEpic ? (
                                <div
                                    data-ignore-click
                                    className={`${styles.taskCardActionItem} ${styles.taskCardEpicWrap}`}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <EpicSelector
                                        projectId={projectId}
                                        value={taskEpicId}
                                        displayOption={taskEpicDisplayOption}
                                        onChange={
                                            isCardInteractive
                                                ? handleEpicChange
                                                : undefined
                                        }
                                        disabled={!isCardInteractive}
                                        compact
                                        placeholder="Select epic"
                                        className={styles.taskCardEpicTrigger}
                                    />
                                </div>
                            ) : null}
                        </Row>
                    )}
                    <Row
                        align={'middle'}
                        className={styles.taskCardRow}
                        style={{ marginTop: '12px' }}
                    >
                        <Row align={'middle'} style={{ gap: '6px', flex: 1 }}>
                            <div
                                data-ignore-click
                                className={styles.taskCardActionItem}
                                style={
                                    !isCardInteractive
                                        ? { pointerEvents: 'none' }
                                        : undefined
                                }
                            >
                                <AssigneeSelectorButton
                                    selectedAssignee={
                                        selectedAssigneeForDisplay
                                    }
                                    onSelect={
                                        isCardInteractive
                                            ? handleAssigneeChange
                                            : undefined
                                    }
                                    projectId={projectId}
                                    filteredAssignees={filteredAssignees}
                                    getAssigneeColor={getAssigneeColor}
                                    getInitials={getInitials}
                                    taskListKind={taskListKind}
                                />
                            </div>
                            <div
                                data-ignore-click
                                className={`${styles.taskCardActionItem} ${styles.taskCardDatePicker}`}
                                style={
                                    !isCardInteractive
                                        ? { pointerEvents: 'none' }
                                        : undefined
                                }
                            >
                                <DateRangePicker
                                    value={{
                                        start: task?.start || null,
                                        end: task?.end || null,
                                    }}
                                    onChange={
                                        isCardInteractive
                                            ? handleDateRangeChange
                                            : undefined
                                    }
                                    placeholder=""
                                />
                            </div>
                        </Row>
                        {task?.commentsLength > 0 && (
                            <Tooltip title="View comments" placement="top">
                                <div
                                    data-ignore-click
                                    className={styles.taskMeta}
                                    style={{
                                        cursor: isPendingCreate
                                            ? 'default'
                                            : 'pointer',
                                        marginTop: 0,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (isPendingCreate) return
                                        onTaskClick?.(task._id, 'comments')
                                    }}
                                >
                                    <span className={styles.commentCount}>
                                        {task?.commentsLength}
                                    </span>
                                    <Icon
                                        icon="iconamoon:comment-light"
                                        width={14}
                                        height={14}
                                        className={styles.commentIcon}
                                    />
                                </div>
                            </Tooltip>
                        )}
                        {!isPendingCreate && (
                            <TaskSubTaskAccordion
                                ref={subTaskAccordionRef}
                                taskId={task._id}
                                projectId={projectId}
                                filteredAssignees={filteredAssignees}
                                getAssigneeColor={getAssigneeColor}
                                getInitials={getInitials}
                                parentTask={task}
                                onTaskUpdate={() => refreshTasks(0)}
                                onTaskClick={onTaskClick}
                                taskListKind={taskListKind}
                                readOnly={readOnlyActions}
                                listQuery={listQuery}
                            />
                        )}
                    </Row>
                    {showCompletionCelebration && (
                        <>
                            {typeof document !== 'undefined' &&
                                createPortal(
                                    <div
                                        className={styles.taskCompleteUnicornFx}
                                        onAnimationEnd={() =>
                                            setShowCompletionCelebration(false)
                                        }
                                    >
                                        <img
                                            className={
                                                styles.taskCompleteUnicornSvg
                                            }
                                            src={unicornFxImage}
                                            alt="Task completed celebration"
                                            aria-hidden="true"
                                        />
                                    </div>,
                                    document.body
                                )}
                        </>
                    )}
                </div>
            </Dropdown>
            {isMainCard && !readOnlyActions ? (
                <TaskParentEpicModal
                    open={parentEpicModalOpen}
                    mode={taskEpicId ? 'change' : 'add'}
                    projectId={projectId}
                    initialEpicId={taskEpicId}
                    taskTitle={task?.title}
                    onCancel={() => setParentEpicModalOpen(false)}
                    onConfirm={handleParentEpicModalConfirm}
                />
            ) : null}
        </>
    )
}

export default ProjectTaskList
